import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import tw from "twin.macro"
import ResponsiveImage from "components/ResponsiveImage"
import { getIconFromUrl } from "utils/iconResolver"
import { linkResolver } from "utils/linkResolver"
import { Icon } from "@clevertrack/shared"
import { IconSizeEnum } from "lib/Icon"
import { Link } from "gatsby"
import { HeroV2Section } from "./HeroV2"

const StyledHeroSection = styled.section`
  min-height: 80vh;
  padding: 0 0 4rem;
  margin-top: 3rem;
  position: relative;
  ${(props) => props.theme.media.tablet_landscape_up`
    margin: 2rem 2rem;
    padding: 4rem 2rem 8rem;
    min-height: 50vh;
    z-index: 50;
  `}
  ${(props) => props.theme.media.desktop_up`
    padding: 4rem 2rem 8rem;
  `};
`

const StyledContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column-reverse;

  ${(props) => props.theme.media.tablet_landscape_up`
    flex-direction: row;
    align-items: center;
  `}
`

const StyledHeadline = styled.h1`
  // margin-top: 0;

  span {
    ${tw`text-brand-500`}
  }
  ${(props) => props.theme.media.tablet_landscape_up`
    line-height: 1.3;
  `}
`

export const StyledHyperlink = styled.a`
  ${tw`text-brand-500`}
  p & {
    ${tw`inline-flex items-center`}
  }

  svg {
    ${tw`ml-4`}
  }
`

const StyledImageContainer = styled.div`
  position: relative;
  z-index: 10;
  ${(props) => props.theme.media.tablet_landscape_up`
    flex: 0 1 50%;
  `}
`

const StyledResponsiveImage = styled(ResponsiveImage)`
  transform-origin: 50%;
  transform: scale3d(1, 1, 1) translate3d(0, 0, 0);

  ${(props) => props.theme.media.tablet_landscape_up`
    transform-origin: top left;
    // transform: scale3d(0.95, 0.95, 0.95) translate3d(-3rem, 3rem, 0);
    opacity: 1;
  `}
`

const StyledImage = styled.div`
  position: relative;
  z-index: 10;
`

const StyledBullet = styled.li`
  // margin: 0 0 0.5rem;
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  line-height: 1.6;

  span {
    width: 2.2rem;
    height: 2.2rem;
    position: relative;
    border-radius: 50%;
    text-align: center;
    margin-right: 1rem;
    margin-top: 0.3rem;
    flex: 0 0 auto;
    align-self: flex-start;
    svg {
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);
      position: absolute;
      z-index: 20;
      fill: currentColor;
      width: 1.2rem;
      height: 1.2rem;
    }
  }

  ${(props) => props.theme.media.tablet_landscape_up`

  `}
`

const StyledContentContainer = styled.div`
  align-self: center;
  position: relative;
  z-index: 50;
  ${tw`shadow-card bg-white`}
  padding: 2rem;
  margin: -80% 2rem 0;
  z-index: 20;

  ${(props) => props.theme.media.tablet_landscape_up`
    padding: 4rem;
    width: 60%;
    margin-top: 0;
    margin-right: -4rem;
    margin-left: 0;
  `}

  p {
    font-size: 1.8rem;
    line-height: 2.6rem;
    font-family: ${(props) => props.theme.fontStacks.light};
  }

  ${(props) => props.theme.media.tablet_landscape_up`
    &:only-child {
      margin: 0 auto;
      text-align: center;
    }
  `}

  ${(props) => props.theme.media.desktop_up`
    margin-left: -4rem;
  `};
`

// New components approach for @prismicio/react
export const htmlSerializer = {
  heading1: ({ children, node, text, key }) => {
    const childrenMap = children
      .flatMap((x) => x)
      .map(
        (x) =>
          typeof x === "string" &&
          x.replace("*", "<span>").replace("*", "</span>")
      )
      .filter(Boolean)
      .join(" ")
    return (
      <StyledHeadline
        key={key || "h1"}
        dangerouslySetInnerHTML={{ __html: childrenMap }}
      />
    )
  },
  heading2: ({ children, node, text, key }) => {
    const childrenMap = children
      .flatMap((x) => x)
      .map(
        (x) =>
          typeof x === "string" &&
          x.replace("*", "<span>").replace("*", "</span>")
      )
      .filter(Boolean)
      .join(" ")
    return (
      <StyledHeadline
        key={key || "h2"}
        as="h2"
        dangerouslySetInnerHTML={{ __html: childrenMap }}
      />
    )
  },
  heading3: ({ children, node, text, key }) => {
    const childrenMap = children
      .flatMap((x) => x)
      .map(
        (x) =>
          typeof x === "string" &&
          x.replace("*", "<span>").replace("*", "</span>")
      )
      .filter(Boolean)
      .join(" ")
    return (
      <StyledHeadline
        key={key || "h3"}
        as="h3"
        dangerouslySetInnerHTML={{ __html: childrenMap }}
      />
    )
  },
  hyperlink: ({ node, children }) => {
    if (node.data?.type === "page") {
      const url = linkResolver(node.data)
      return (
        <StyledHyperlink as={Link} to={url}>
          {children}
          <Icon icon="long-arrow-right" />
        </StyledHyperlink>
      )
    }
    return (
      <StyledHyperlink
        href={node.data?.url}
        rel="noopener"
        target="_blank"
      >
        {children}
      </StyledHyperlink>
    )
  }
}


export const bulletsSerializer = (color, bgColor, icon) => ({
  list: ({ children }) => <ul tw="list-none px-2 md:(px-8)">{children}</ul>,
  listItem: ({ children }) => (
    <StyledBullet>
      <span css={{ "background-color": bgColor, color }}>
        <Icon icon={icon} size={IconSizeEnum.SM} />
      </span>
      {children}
    </StyledBullet>
  ),
  ...htmlSerializer
})

const gridPhone = `"content"`
const gridTabletPortrait = `"content content content . image image image"`
const gridFull = `". content content content content content ."`

function HeroSection({ isHome, ctas, ...props }) {
  const {
    theme,
    content,
    bullets,
    bullet_color,
    bullet_background_color,
    bullet_icon_alt,
    headline,
    image,
    pre_headline,
    background_image,
    background_video,
  } = props
  const icon = getIconFromUrl(bullet_icon_alt?.url)
  return (
    <HeroV2Section
      content={content}
      {...{
        pre_headline,
        headline,
        bullets,
        bullet_color,
        bullet_background_color,
        background_image,
        image,
        ctas,
        theme,
        icon,
        background_video,
      }}
    />
  )
}

export default HeroSection

HeroSection.defaultProps = {}
HeroSection.propTypes = {
  children: PropTypes.node,
}
